import axios from "axios";
import { defineStore } from "pinia";
import { useMessage } from "./app-toast";

export interface UploadStoreState {
    uploading: boolean;
    progress: number;
    uploadController: AbortController | null;
}

export const useUploadStore = defineStore("UploadStore", {
    state: (): UploadStoreState => ({
        uploading: false,
        progress: 0,
        uploadController: null,
    }),
    actions: {
        isFileSizeAllowed(file: File, fileMaxSize = 500) {
            if (!file) return false;

            const maxBytes = fileMaxSize * 1024 * 1024;
            return file.size <= maxBytes;
        },

        cancelUpload() {
            this.uploadController?.abort();
        },

        async directUpload(file: File, folder?: string): Promise<string | null> {
            const { $api } = useNuxtApp()
            const toast = useMessage()
            const allowed = this.isFileSizeAllowed(file);
            if (!allowed) {
                toast.add(
                    "The file is too large. Please upload a file smaller than 120 MB.",
                    "error",
                );

                return null;
            }

            this.uploading = true;
            this.progress = 0;
            this.uploadController = new AbortController();
            try {
                const data = await $api<any>("upload-url", {
                    params: { filename: file.name, folder },
                });

                const { signedUrl, publicUrl } = data;
                await axios.put(signedUrl, file, {
                    headers: { "Content-Type": file.type },
                    signal: this.uploadController.signal,
                    onUploadProgress: (event) => {
                        if (event.total) {
                            this.progress = Math.round((event.loaded / event.total) * 100);
                        }
                    },
                });

                toast.add("File uploaded successfully", "success");
                return publicUrl;
            } catch (e: any) {
                if (e.name === "CanceledError") {
                    toast.add("Upload canceled", "success");
                } else {
                    toast.error(e)
                }
                return null;
            } finally {
                this.uploading = false;
                this.progress = 0;
                this.uploadController = null;
            }
        },
        async optimizeAndUpload(file: File, folder: string, fileName: string): Promise<string | null> {
            const { $api } = useNuxtApp()
            const toast = useMessage()

            if (!this.isFileSizeAllowed(file)) {
                toast.add("The file is too large. Please upload a file smaller than 120 MB.", "error");
                return null;
            }

            this.uploading = true;

            try {
                const formData = new FormData();
                formData.append("image", file);
                formData.append("fileName", fileName);
                formData.append("folder", folder);

                const response = await $api<any>("/optimize-upload", {
                    method: "POST",
                    body: formData
                });

                if (response.success) {
                    if (response.code) {
                        toast.add(response.code, "success");
                    } else {
                        toast.add(response.message, "success");
                    }

                    return response.data.fileUrl;
                }

                // If success is false, show the backend's error message
                toast.add(response.message || "Upload failed", "error");
                return null;

            } catch (e: any) {
                const msg = e.response?._data?.message || e.message || "Upload failed";
                toast.add(msg, "error");
                return null;
            } finally {
                this.uploading = false;
            }
        },
        // async fileUpload(file: File, folder?: string): Promise<string | null> {
        //     const messageStore = useMessageStore();
        //     this.uploading = true;

        //     try {
        //         const formData = new FormData();
        //         formData.append("file", file);
        //         if (folder) formData.append("folder", folder);

        //         const { data } = await API_URL.post("/upload", formData, {
        //             headers: { "Content-Type": "multipart/form-data" },

        //         });

        //         const url = data.fileUrl || null;
        //         toast.add(
        //             url ? "File uploaded successfully" : "Something went wrong",
        //             url ? "success" : "warning",
        //         );

        //         return url;
        //     } catch (e) {
        //         messageStore.handleErrorMassage(e);
        //         return null;
        //     } finally {
        //         this.uploading = false;
        //     }
        // },
    },
});
